import { IconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { faFile, faFolderOpen } from '@fortawesome/free-regular-svg-icons';
import { faEdit, faEllipsisV, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Dropdown, IconButton, Menu, MenuButton, MenuItem } from '@mui/joy';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { S_Link } from '../../../../design/links/S_Link';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { S_ClampLines } from '../../../../design/misc/S_ClampLines';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { useAttachmentsPermissions } from '../../../../modules/attachments/Attachments.permissions';
import { RenameAttachmentMenuItemContainer } from '../../../../modules/attachments/rename/ui/dropdownItem/RenameAttachmentDropdownItem.container';
import { LazyImage } from '../../../components/LazyImage';
import { EnableFor } from '../../../containers/EnableForRole';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { ExtensionDefinitions, FileExtensionDefinition } from '../../../helpers/fileExtensions.helpers';
import { bytesToMBsOrGBs } from '../../../helpers/string.helper';
import { Home } from '../../../routes/config/Home';
import { AttachmentType, IAttachment } from '../attachments.store';

library.add(faUnlink, faEdit, faFile, faFolderOpen);

interface IAttachmentThumbnailProps {
  attachment: IAttachment;
  authToken: string;
  isChecked: boolean;
  workspaceId: string;
  onCheck: (id: string, isBatch: boolean) => void;
  onFileDownload: (id: string) => Promise<void>;
  onDelete: (id: string) => void;
  getThumbnailUrl: (id: string) => string;
}

export const AttachmentThumbnail = observer((props: IAttachmentThumbnailProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useAttachmentsPermissions();

  const getPreviewUrl = (): string => {
    switch (props.attachment.type) {
      case AttachmentType.FOLDER:
        return Home.Attachments.List.Folder.withParams({
          workspaceId: props.workspaceId,
          attachmentId: props.attachment.id,
        });
        break;
      case AttachmentType.IMAGE_FILE:
        return Home.Attachments.List.Preview.withParams({
          attachmentId: props.attachment.id,
          workspaceId: props.workspaceId,
          parentId: props.attachment.parentId || '',
        });
      case AttachmentType.FILE:
      default:
        throw new Error('Invalid attachment type for preview URL');
    }
  };

  const getFileTypeAttachmentIcon = (): IconDefinition => {
    const fileExtension = props.attachment.name.split('.').pop();
    let icon = faFile;

    if (!fileExtension) {
      return icon;
    }

    ExtensionDefinitions.forEach((ed: FileExtensionDefinition) => {
      if (ed.extensions.includes(fileExtension)) {
        icon = ed.icon;
      }
    });

    return icon;
  };

  const renderAttachmentImage = (): ReactElement => {
    switch (props.attachment.type) {
      case AttachmentType.IMAGE_FILE: {
        const image = (
          <LazyLoadComponent threshold={300}>
            <LazyImage
              source={props.getThumbnailUrl(props.attachment.id)}
              headers={new Headers({ Authorization: `Bearer ${props.authToken}` })}
            />
          </LazyLoadComponent>
        );

        return (
          <S_Link2
            color='text'
            to={getPreviewUrl()}
          >
            {image}
          </S_Link2>
        );
      }
      case AttachmentType.FOLDER:
        return (
          <S_Link2
            color='text'
            to={getPreviewUrl()}
          >
            <FontAwesomeIcon
              icon={faFolderOpen}
              className='svg-icon'
            />
          </S_Link2>
        );
      case AttachmentType.FILE: {
        const icon = getFileTypeAttachmentIcon();
        return (
          <FontAwesomeIcon
            icon={icon}
            className='svg-icon'
            onClick={(): void => {
              void props.onFileDownload(props.attachment.id);
            }}
          />
        );
      }
      default:
        return (
          <FontAwesomeIcon
            icon={faFile}
            className='svg-icon'
          />
        );
    }
  };

  const renderName = (): ReactElement => {
    switch (props.attachment.type) {
      case AttachmentType.FOLDER:
      case AttachmentType.IMAGE_FILE: {
        return (
          <S_Link2
            color='text'
            className='persist-reduntant-spaces'
            to={getPreviewUrl()}
          >
            <S_ClampLines
              lines={2}
              text={props.attachment.name}
            />
          </S_Link2>
        );
      }
      case AttachmentType.FILE:
        return (
          <S_Link
            color='text'
            className='persist-reduntant-spaces'
            onClick={(): void => {
              void props.onFileDownload(props.attachment.id);
            }}
          >
            <S_ClampLines
              lines={2}
              text={props.attachment.name}
            />
          </S_Link>
        );
    }

    return (
      <div className='disabled persist-reduntant-spaces'>
        <S_ClampLines
          lines={2}
          text={props.attachment.name}
        />
      </div>
    );
  };

  const renderSize = (): string =>
    props.attachment.type === AttachmentType.FOLDER
      ? `${bytesToMBsOrGBs(props.attachment.size)} (${props.attachment.items} ${t('items')})`
      : bytesToMBsOrGBs(props.attachment.size);

  return (
    <div className='thumb'>
      <div className='image-container'>
        <div className={classNames('image-wrapper', { icon: props.attachment.type !== AttachmentType.IMAGE_FILE })}>{renderAttachmentImage()}</div>
        <Dropdown>
          <MenuButton
            className='thumb-dropdown'
            slots={{ root: IconButton }}
            slotProps={{ root: { sx: { '&:hover': { backgroundColor: 'transparent' } } } }}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </MenuButton>
          <Menu placement='bottom-start'>
            <RenameAttachmentMenuItemContainer
              id={props.attachment.id}
              name={props.attachment.name}
              parentId={props.attachment.parentId}
            />
            <EnableFor hasPermission={permissions.canDeleteAttachments()}>
              <MenuItem
                onClick={(): void => {
                  props.onDelete(props.attachment.id);
                }}
              >
                <TrashIcon /> {t('delete_attachment')}
              </MenuItem>
            </EnableFor>
          </Menu>
        </Dropdown>
        <div
          className='thumb-checkbox'
          title={props.attachment.name}
        >
          <S_Checkbox
            checked={props.isChecked}
            onClick={(e): void => {
              props.onCheck(props.attachment.id, e.nativeEvent.shiftKey);
            }}
            value={props.attachment.id}
            id={props.attachment.id}
            className={classNames({ visible: props.isChecked })}
          />
        </div>
      </div>
      <div className='description'>
        <div className='file-name'>{renderName()}</div>
        <div className='attributes'>
          <div className='attribute'>
            {t('created')}:
            <span
              title={toLocaleDateTimeString(props.attachment.createdDate)}
              className='attribute-value'
            >
              {toLocaleDateTimeString(props.attachment.createdDate)}
            </span>
          </div>
          <div className='attribute'>
            {t('size')}:
            <span
              title={renderSize()}
              className='attribute-value'
            >
              {renderSize()}
            </span>
          </div>
          {props.attachment.author && (
            <div className='attribute'>
              {t('author')}:
              <span
                className='attribute-value'
                title={props.attachment.author}
              >
                {props.attachment.author}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
